import { useCallback, useEffect, useState } from "react";
import api from "../utils/api/axiosInstance";
import {
  getAiSessionDependencyKey,
  readHomeChatMatchCache,
  syncActiveFeedMatchToLocalCache,
} from "../utils/homeChatMatchPersistence";

let activeFeedFetchPromise = null;
let activeFeedFetchUserId = null;

const fetchActiveFeedMatchOnce = async (userId) => {
  if (!userId) return null;

  if (activeFeedFetchPromise && activeFeedFetchUserId === userId) {
    return activeFeedFetchPromise;
  }

  activeFeedFetchUserId = userId;
  activeFeedFetchPromise = api
    .get("/analytics/active-feed-match")
    .then((res) => res.data?.activeMatch || null)
    .catch(() => null)
    .finally(() => {
      activeFeedFetchPromise = null;
      activeFeedFetchUserId = null;
    });

  return activeFeedFetchPromise;
};

/**
 * Shared active home AI session — same cache the feed and PDP use.
 * @param {object|null} user
 * @param {{ syncServer?: boolean }} options — only one caller per page should syncServer: true
 */
const useHomeChatMatchSession = (user, options = {}) => {
  const { syncServer = true } = options;
  const userId = user?._id || user?.id || null;
  const [aiSession, setAiSession] = useState(() => readHomeChatMatchCache(userId));
  const [sessionKey, setSessionKey] = useState(() =>
    getAiSessionDependencyKey(readHomeChatMatchCache(userId)),
  );

  const refreshSession = useCallback(() => {
    const next = readHomeChatMatchCache(userId);
    const nextKey = getAiSessionDependencyKey(next);
    setSessionKey((prevKey) => {
      if (prevKey === nextKey) return prevKey;
      setAiSession(next);
      return nextKey;
    });
  }, [userId]);

  useEffect(() => {
    refreshSession();

    let cancelled = false;

    if (userId && syncServer) {
      (async () => {
        const match = await fetchActiveFeedMatchOnce(userId);
        if (cancelled || !match) return;
        if (syncActiveFeedMatchToLocalCache(match, userId)) {
          refreshSession();
        }
      })();
    }

    const onStorage = (event) => {
      if (
        event.key === "homeChatMatchCache" ||
        event.key === "wizardAnswers" ||
        event.key === "matchProfile"
      ) {
        refreshSession();
      }
    };

    const onChatUpdated = () => refreshSession();

    window.addEventListener("storage", onStorage);
    window.addEventListener("homeChatMatchUpdated", onChatUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("homeChatMatchUpdated", onChatUpdated);
    };
  }, [refreshSession, syncServer, userId]);

  return aiSession;
};

export default useHomeChatMatchSession;
